import { COURSE_LEVEL_VALUES, CourseSortBy, CourseFilterKey } from '@repo/types';
import { COURSE_KIND } from '@theailab-dev/parchment';
import React, { useState, useEffect } from 'react';
import { HiSearch, HiPlus, HiFilter, HiRefresh } from 'react-icons/hi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TablePageLayout } from '../../components/Layout/TablePageLayout';
import {
  Button,
  Input,
  Card,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useCourses } from '../../hooks/useCourses';

/**
 * Courses 컴포넌트 - 강의 목록 페이지
 */
const Courses: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL 쿼리 파라미터 읽기
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentLimit = parseInt(searchParams.get('limit') || '20', 10);
  const filterType = searchParams.get('filterType') || '';
  const filterValue = searchParams.get('filterValue') || '';
  const searchKeyword = searchParams.get('search') || '';

  // 검색 입력 필드 local state
  const [searchQuery, setSearchQuery] = useState(searchKeyword);

  const queryParams = {
    page: currentPage,
    limit: currentLimit,
    sortBy: CourseSortBy.CREATED_AT,
    sortOrder: 'DESC' as const,
    filterKey: searchKeyword
      ? CourseFilterKey.SEARCH
      : filterType && filterValue
        ? filterType === 'search'
          ? CourseFilterKey.SEARCH
          : filterType === 'level'
            ? CourseFilterKey.LEVEL
            : filterType === 'kind'
              ? CourseFilterKey.KIND
              : undefined
        : undefined,
    filterValue: searchKeyword || (filterType && filterValue ? filterValue : undefined),
  };

  const { data, isLoading, isError, error, refetch } = useCourses(queryParams);

  useEffect(() => {
    setSearchQuery(searchKeyword);
  }, [searchKeyword]);

  useEffect(() => {
    if (!searchParams.has('page')) {
      setSearchParams({ page: '1' }, { replace: true });
    }
  }, []);

  const courses = data?.data || [];
  const meta = data?.meta;
  const page = meta?.page || 1;
  const limit = meta?.limit || 20;
  const total = meta?.total || 0;
  const totalPages = meta?.totalPages || 1;

  const handleSearch = () => {
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('limit', currentLimit.toString());
    if (searchQuery.trim()) {
      params.set('search', searchQuery.trim());
    }
    if (filterType && filterValue) {
      params.set('filterType', filterType);
      params.set('filterValue', filterValue);
    }
    setSearchParams(params);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleFilterTypeChange = (type: string) => {
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('limit', currentLimit.toString());
    if (type) {
      params.set('filterType', type);
    }
    if (searchKeyword) {
      params.set('search', searchKeyword);
    }
    setSearchParams(params);
  };

  const handleFilterValueChange = (value: string) => {
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('limit', currentLimit.toString());

    if (value && filterType) {
      params.set('filterType', filterType);
      params.set('filterValue', value);
    }

    if (searchKeyword) {
      params.set('search', searchKeyword);
    }

    setSearchParams(params);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('limit', currentLimit.toString());
    if (filterType && filterValue) {
      params.set('filterType', filterType);
      params.set('filterValue', filterValue);
    }
    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const handleLimitChange = (newLimit: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('limit', newLimit.toString());
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleCourseClick = (courseId: number) => {
    navigate(`/dashboard/courses/${courseId}`);
  };

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return '-';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return '-';

      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    } catch {
      return '-';
    }
  };

  const getLevelBadgeVariant = (level: string) => {
    switch (level) {
      case 'BEGINNER':
        return 'success';
      case 'INTERMEDIATE':
        return 'warning';
      case 'ADVANCED':
        return 'danger';
      default:
        return 'neutral';
    }
  };

  if (isError) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-bold text-slate-900">오류가 발생했습니다</h3>
          <p className="mb-6 text-slate-500">
            {error instanceof Error ? error.message : '강의 목록을 불러오는데 실패했습니다.'}
          </p>
          <Button onClick={() => refetch()} variant="outline">
            다시 시도
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <TablePageLayout
      title="강의 목록"
      description="전체 강의를 관리하고 모니터링하세요"
      action={
        <Button onClick={() => navigate('/dashboard/courses/new')} leftIcon={<HiPlus />}>
          강의 추가
        </Button>
      }
      filters={
        <>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <HiFilter className="text-slate-400" />
            </div>
            <select
              value={filterType}
              onChange={(e) => handleFilterTypeChange(e.target.value)}
              className="cursor-pointer appearance-none rounded-lg border border-slate-200 bg-white py-2 pr-8 pl-9 text-sm text-slate-700 transition-colors hover:border-slate-300 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="">전체 필터</option>
              <option value="level">레벨</option>
              <option value="kind">종류</option>
            </select>
          </div>

          <select
            value={filterValue}
            onChange={(e) => handleFilterValueChange(e.target.value)}
            disabled={!filterType}
            className="min-w-[140px] cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 transition-colors hover:border-slate-300 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-slate-50 disabled:text-slate-400">
            <option value="">선택하세요</option>
            {filterType === 'level' &&
              COURSE_LEVEL_VALUES.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            {filterType === 'kind' &&
              Object.values(COURSE_KIND).map((kind) => (
                <option key={kind} value={kind}>
                  {kind}
                </option>
              ))}
          </select>

          {(filterType || searchKeyword) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSearch}
              leftIcon={<HiRefresh />}
              className="text-slate-500">
              초기화
            </Button>
          )}
        </>
      }
      search={
        <>
          <div className="relative w-full lg:w-72">
            <Input
              placeholder="강의 제목, 강사명 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              leftIcon={<HiSearch />}
              className="w-full"
            />
          </div>
          <Button onClick={handleSearch} variant="secondary">
            검색
          </Button>
        </>
      }
      isLoading={isLoading}
      currentPage={page}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      totalItems={total}
      limit={limit}
      onLimitChange={handleLimitChange}>
      {isLoading ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
          <LoadingSpinner />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">ID</TableHead>
              <TableHead className="min-w-[200px]">강의 정보</TableHead>
              <TableHead className="min-w-[120px] whitespace-nowrap">레벨</TableHead>
              <TableHead className="whitespace-nowrap">종류</TableHead>
              <TableHead className="whitespace-nowrap">강의 수</TableHead>
              <TableHead className="whitespace-nowrap">카테고리</TableHead>
              <TableHead className="min-w-[150px] whitespace-nowrap">강사</TableHead>
              <TableHead className="whitespace-nowrap">생성일</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {courses.map((course) => (
              <TableRow key={course.id} onClick={() => handleCourseClick(course.id)}>
                <TableCell className="text-slate-500">#{course.id}</TableCell>
                <TableCell>
                  <div className="font-medium text-slate-900 transition-colors group-hover:text-blue-600">
                    {course.title}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={getLevelBadgeVariant(course.level || '')}>{course.level || '-'}</Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="neutral">{course.kind}</Badge>
                </TableCell>
                <TableCell>{course.totalLessonCount}개</TableCell>
                <TableCell>{course.category?.title || '-'}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-xs font-medium text-indigo-600">
                      {course.instructor?.name?.[0] || 'T'}
                    </div>
                    {course.instructor?.name || '-'}
                  </div>
                </TableCell>
                <TableCell nowrap className="text-slate-500">
                  {formatDate(course.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {!isLoading && courses.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-50">
            <svg className="h-8 w-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-900">강의가 없습니다</h3>
          <p className="mt-1 text-slate-500">새로운 강의를 추가하거나 검색 조건을 변경해보세요.</p>
          <Button variant="outline" className="mt-4" onClick={handleClearSearch}>
            필터 초기화
          </Button>
        </div>
      )}
    </TablePageLayout>
  );
};

export default Courses;
